import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertTriangle, RefreshCw, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (map) => Object.entries(map || {})
  .map(([cur, val]) => `${Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${cur}`)
  .join(' + ') || '—';

/**
 * What hired-in plant is actually costing. Shown per project so an item
 * quietly racking up a day rate three weeks after the job finished is
 * impossible to miss — and flagged outright once the hire has cost more
 * than buying the thing would have.
 *
 * `projectId` scopes it to one project (Project dialog); omit it for the
 * company-wide roll-up (Inventory dashboard).
 */
const HireCosts = ({ projectId = null, compact = false }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inventory/hire-costs`, {
        params: projectId ? { project_id: projectId } : {},
      });
      setData(r.data);
      setDenied(false);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
      setData(null);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (denied) return null;

  if (loading && !data) {
    return (
      <Card data-testid="hire-costs-card">
        <CardHeader className="pb-3"><CardTitle className="text-base">{t('inv.hire.title')}</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-muted-foreground">{t('common.loading')}</p></CardContent>
      </Card>
    );
  }

  const items = data?.items || [];

  return (
    <Card data-testid="hire-costs-card">
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-4 w-4" /> {t('inv.hire.title')}
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {projectId
              ? t('inv.hire.subtitleProject', { date: data?.as_of })
              : t('inv.hire.subtitle', { date: data?.as_of })}
            {data?.without_rate ? ` ${t('inv.hire.withoutRate', { count: data.without_rate })}` : ''}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-hire-costs-btn">
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> {t('common.refresh')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {items.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground" data-testid="no-hire-costs">
            {projectId ? t('inv.hire.emptyProject') : t('inv.hire.empty')}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{t('inv.hire.spendToDate')}</p>
                <p className="text-lg font-bold" data-testid="hire-total">{money(data.totals_by_currency)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{t('inv.hire.stillOnSite')}</p>
                <p className="text-lg font-bold">{data.still_out}</p>
              </div>
              <div className={`rounded-lg border p-3 ${data.overdue > 0 ? 'border-red-200 bg-red-50' : ''}`}>
                <p className="text-xs text-muted-foreground">{t('inv.hire.pastReturnDate')}</p>
                <p className={`text-lg font-bold ${data.overdue > 0 ? 'text-red-700' : ''}`}>{data.overdue}</p>
              </div>
              <div className={`rounded-lg border p-3 ${(data.flagged || []).length ? 'border-amber-200 bg-amber-50' : ''}`}>
                <p className="text-xs text-muted-foreground">{t('inv.hire.overBuying')}</p>
                <p className={`text-lg font-bold ${(data.flagged || []).length ? 'text-amber-800' : ''}`}>
                  {(data.flagged || []).length}
                </p>
              </div>
            </div>

            {!projectId && (data.projects || []).length > 0 && (
              <div>
                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('inv.hire.byProject')}
                </h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('inv.hire.project')}</TableHead>
                      <TableHead className="hidden sm:table-cell w-[90px]">{t('inv.hire.items')}</TableHead>
                      <TableHead className="w-[150px] text-right">{t('inv.hire.spendToDate')}</TableHead>
                      <TableHead className="hidden md:table-cell w-[160px] text-right">{t('inv.hire.ifRunToEnd')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.projects.map((p) => (
                      <TableRow key={p.project_id || 'unassigned'}
                        data-testid={`hire-project-row-${p.project_id || 'unassigned'}`}>
                        <TableCell className="break-words text-sm font-medium">
                          {p.project_name}
                          {p.overdue > 0 && (
                            <Badge variant="destructive" className="ml-2 text-[10px]">
                              {t('inv.hire.overdueCount', { count: p.overdue })}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-sm">{p.items}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">{money(p.cost_by_currency)}</TableCell>
                        <TableCell className="hidden md:table-cell text-right text-sm text-muted-foreground">
                          {money(p.projected_by_currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {!compact && (
              <div>
                <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('inv.hire.byItem')}
                </h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[110px]">{t('inv.assets.invNo')}</TableHead>
                      <TableHead>{t('inv.hire.item')}</TableHead>
                      <TableHead className="hidden lg:table-cell">{t('inv.hire.hireCompany')}</TableHead>
                      <TableHead className="hidden sm:table-cell w-[110px]">{t('inv.hire.rate')}</TableHead>
                      <TableHead className="hidden md:table-cell w-[70px] text-right">{t('inv.hire.days')}</TableHead>
                      <TableHead className="w-[130px] text-right">{t('inv.hire.costSoFar')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((r) => (
                      <TableRow key={r.asset_id} data-testid={`hire-item-row-${r.inventory_number}`}>
                        <TableCell className="font-mono text-xs">{r.inventory_number}</TableCell>
                        <TableCell className="break-words text-sm">
                          {r.name}
                          <span className="block text-xs text-muted-foreground">
                            {[!projectId ? r.project_name : null,
                              r.still_out ? t('inv.hire.stillOut') : t('inv.hire.offHired')].filter(Boolean).join(' · ')}
                          </span>
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {r.overdue && (
                              <Badge variant="destructive" className="text-[10px]">
                                {t('inv.hire.pastReturn', { date: String(r.expected_return_date || '').slice(0, 10) })}
                              </Badge>
                            )}
                            {r.exceeds_purchase_value && (
                              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-900">
                                <AlertTriangle className="mr-1 h-3 w-3" />
                                {t('inv.hire.exceedsPurchase')}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell break-words text-sm">
                          {r.hire_company || '—'}
                          {r.order_reference && (
                            <span className="block text-xs text-muted-foreground">{r.order_reference}</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell whitespace-nowrap text-sm">
                          {r.rate} {r.currency}/{t(`inv.ownership.periodShort.${r.rate_period}`, { defaultValue: r.rate_period })}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-right text-sm">{r.days}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">
                          {Number(r.cost_so_far).toLocaleString(undefined, { maximumFractionDigits: 2 })} {r.currency}
                          {r.projected_total && (
                            <span className="block text-xs font-normal text-muted-foreground">
                              {t('inv.hire.projectedBy', {
                                amount: Number(r.projected_total).toLocaleString(undefined, { maximumFractionDigits: 2 }),
                                date: r.agreed_end,
                              })}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default HireCosts;
